/**
 * Corporate company_email rules (keep CONSUMER_EMAIL_DOMAINS / SOCIAL_HOST_SUFFIXES
 * aligned with workbrook-api/app/Support/EmployerCorporateEmailValidator.php).
 */

const CONSUMER_EMAIL_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.uk",
  "yahoo.fr",
  "yahoo.de",
  "ymail.com",
  "rocketmail.com",
  "hotmail.com",
  "hotmail.co.uk",
  "outlook.com",
  "outlook.fr",
  "live.com",
  "live.co.uk",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "protonmail.com",
  "proton.me",
  "pm.me",
  "gmx.com",
  "gmx.de",
  "gmx.net",
  "mail.com",
  "email.com",
  "yandex.com",
  "yandex.ru",
  "fastmail.com",
  "fastmail.fm",
  "hey.com",
  "tutanota.com",
  "tutanota.de",
  "duck.com",
  "qq.com",
  "163.com",
  "126.com",
];

const SOCIAL_HOST_SUFFIXES = [
  "linkedin.com",
  "facebook.com",
  "fb.com",
  "instagram.com",
  "twitter.com",
  "x.com",
  "tiktok.com",
];

export function normalizeDomain(host) {
  let h = (host || "").trim().toLowerCase();
  if (h.startsWith("www.")) {
    h = h.slice(4);
  }
  return h;
}

export function emailDomain(email) {
  const e = (email || "").trim().toLowerCase();
  const at = e.lastIndexOf("@");
  if (at < 0) return null;
  const d = e.slice(at + 1).trim();
  return d || null;
}

export function isConsumerEmailDomain(domain) {
  const d = normalizeDomain(domain);
  return CONSUMER_EMAIL_DOMAINS.includes(d);
}

function hostEndsWithSocialSuffix(host) {
  const h = normalizeDomain(host);
  return SOCIAL_HOST_SUFFIXES.some(
    (suffix) => h === suffix || h.endsWith(`.${suffix}`)
  );
}

export function isSocialProfileHost(host) {
  return hostEndsWithSocialSuffix(host);
}

export function parseWebsiteHost(url) {
  if (url == null) return null;
  let u = String(url).trim();
  if (!u) return null;
  if (!/^[a-z][a-z0-9+\-.]*:/i.test(u)) {
    u = `https://${u}`;
  }
  try {
    const parsed = new URL(u);
    if (!parsed.hostname) return null;
    return parsed.hostname.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * @returns {{ company_email: string|null, website_url: string|null }}
 */
export function getEmployerCorporateFieldErrors({ company_email, website_url }) {
  const empty = { company_email: null, website_url: null };
  const email = (company_email || "").trim();
  if (!email) return empty;

  const ed = emailDomain(email);
  if (!ed) return empty;

  if (isConsumerEmailDomain(ed)) {
    return {
      company_email:
        "Please use a work email on your company's domain (personal addresses like Gmail are not accepted).",
      website_url: null,
    };
  }

  const siteRaw = (website_url || "").trim();
  const siteHost = parseWebsiteHost(siteRaw);
  if (!siteHost || isSocialProfileHost(siteHost)) {
    return empty;
  }

  const normalizedSite = normalizeDomain(siteHost);
  if (!normalizedSite) return empty;

  if (normalizeDomain(ed) !== normalizedSite) {
    return {
      company_email: `Company email must use @${normalizedSite} to match your company website.`,
      website_url: `Use your company homepage so it matches your email (e.g. name@${normalizedSite}).`,
    };
  }

  return empty;
}
